import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { returnUrlQueryParams } from '../utilities/return-url';

export const LoggedInGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const router = inject(Router);
  // Brings a deep link (an email, a bookmark, another tab) back to the page it asked for once the
  // user has signed in, instead of dropping them on their homepage.
  const toLogin = () => router.navigate(['/login'], { queryParams: returnUrlQueryParams(state.url) });

  // The token's own expiry is readable client-side (unlike the HttpOnly cookie it replaced), so
  // an expired session can be caught here without a round trip. The heartbeat below still runs
  // for anything the token's expiry can't tell us on its own: revocation, deactivation, or an
  // admin-forced logout - see TokenHandler.CheckUserStatus on the backend.
  if (tokenService.isExpired()) {
    toLogin();
    return of(false);
  }

  return authService.heartbeat().pipe(
    map(result => {
      // This heartbeat reports a moved organization the same way the timer's does. Without handling
      // it here the report is thrown away on whichever navigation happened to trigger it, and
      // whether the user hears about it depends on which heartbeat won the race.
      if (result?.organizationChanged) {
        authService.applyExternalOrganizationChange(
          result.organizationId,
          result.organizationName,
        );
      }
      return true;
    }),
    catchError((err: HttpErrorResponse) => {
      // A 401 means the session is gone: AuthInterceptor has already cleared it down and
      // redirected. Anything else - offline, a 500 - is a fault, not proof the user is logged
      // out, so log it rather than letting it masquerade as an expired session.
      if (err?.status !== 401) {
        console.error('Session check failed', err);
        toLogin();
      }
      return of(false);
    })
  );
};
